import { useCallback, useState } from "react";
import { throwResponseError } from "../../utils/api-error-utils";
import { getAuthParams } from "../../utils/pacto-rebates-utils";
import {
	ASSESSMENT_DEFAULT_PAGE_SIZE,
	buildAssessmentsParams,
	formatAssessmentsResponse
} from "../utils/installments-assessment-utils";

const createInitialPagination = (
	size = ASSESSMENT_DEFAULT_PAGE_SIZE
) => ({
	totalElements: 0,
	totalPages: 0,
	page: 0,
	size
});

export function useAssessmentsHistory() {
	const [assessments, setAssessments] = useState([]);
	const [pagination, setPagination] = useState(
		() => createInitialPagination()
	);
	const [statistics, setStatistics] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const fetchAssessmentsHistory = useCallback(async ({
		cycleId,
		status,
		filters = {},
		page = 0,
		size = ASSESSMENT_DEFAULT_PAGE_SIZE,
		sort = "dataApuracao,desc"
	} = {}) => {
		try {
			setLoading(true);
			setError(null);

			const { accessToken, clientId } = getAuthParams();

			const params = buildAssessmentsParams({
				cycleId,
				status,
				filters,
				page,
				size,
				sort
			});

			const response = await fetch(
				process.env.REACT_APP_ESSILOR_API +
					`pacto-rebates-apuracao/v1/apuracoes/historico?${params.toString()}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						"access_token": accessToken,
						"client_id": clientId
					}
				}
			);

			if (!response.ok) {
				await throwResponseError(
					response,
					"Não foi possível carregar o histórico de apurações."
				);
			}

			const data = await response.json();
			const formattedResponse = formatAssessmentsResponse(data);

			setAssessments(formattedResponse.items);
			setPagination(formattedResponse.pagination);
			setStatistics(formattedResponse.statistics);

			return {
				ok: true,
				assessments: formattedResponse.items,
				pagination: formattedResponse.pagination,
				statistics: formattedResponse.statistics,
				empty: formattedResponse.items.length === 0
			};
		} catch (requestError) {
			const errorMessage =
				requestError.message ||
				"Não foi possível carregar o histórico de apurações.";

			setAssessments([]);
			setPagination(createInitialPagination(size));
			setStatistics(null);
			setError(errorMessage);

			return {
				ok: false,
				reason: "request",
				error: errorMessage
			};
		} finally {
			setLoading(false);
		}
	}, []);

	const clearAssessments = useCallback(() => {
		setAssessments([]);
		setPagination(createInitialPagination());
		setStatistics(null);
		setError(null);
	}, []);

	return {
		assessments,
		pagination,
		statistics,
		loading,
		error,
		refetch: fetchAssessmentsHistory,
		clearAssessments
	};
}